import { useMemo } from 'react';
import { TFunction } from 'types/i18n';
import { isPlainObject } from './lib/translationGuards';

/**
 * Read a translation value as a plain `Record<string, unknown>`, with a
 * defensive fallback to `{}` when the translation file resolves to a
 * string, array, `null`, or `undefined`.
 *
 * Companion to `useTranslatedArray` for translations whose root is an
 * object (e.g. `{ title, subtitle, items }`). Centralizes the
 * `t(key, { returnObjects: true })` + `isPlainObject` guard that
 * otherwise tends to get re-implemented at every call site.
 *
 * Silent on failure — there is no `onInvalid` here because:
 *   1) Missing/malformed *object* shapes have always been treated as a
 *      soft fallback in the existing consumers (title falls back to a
 *      default copy), unlike ESCR-1618 which was about arrays.
 *   2) When paired with `useTranslatedArray` on the same key, the array
 *      hook is already responsible for emitting the consistent log line.
 *      Logging here too would double-fire.
 */
const useTranslatedRecord = (
  key: string,
  t: TFunction,
): Record<string, unknown> =>
  useMemo(() => {
    if (!key) return {};
    const resolved = t(key, { returnObjects: true });
    return isPlainObject(resolved) ? resolved : {};
  }, [key, t]);

export default useTranslatedRecord;
